import type { AxiosError } from "axios";
import axios from "axios";
import { logger } from "@sentry/solid";
import { isNil } from "lodash-es";

export class MoreAppError extends Error {
  code?: string;
  status: number;
  data?: unknown;
  rawData?: unknown;
  traceId?: string;

  constructor({
    code,
    message,
    status,
    data,
    rawData,
    traceId,
  }: {
    code?: string;
    message: string;
    status: number;
    data?: unknown;
    rawData?: unknown;
    traceId?: string;
  }) {
    super(message);

    this.code = code;
    this.status = status;
    this.data = data;
    this.rawData = rawData;
    this.traceId = traceId;

    // This line is needed to restore the correct prototype chain.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export const handleApiError = (e: AxiosError<MoreAppError>): MoreAppError => {
  if (axios.isAxiosError(e)) {
    if (e.code === "ERR_CANCELED") {
      return new MoreAppError({
        code: "REQUEST_ABORTED",
        message: "User aborted request",
        status: -1,
      });
    }

    const { response } = e;
    if (isNil(response)) {
      // This can happen in at least the following cases;
      // - User is offline
      // - DNS issues
      return new MoreAppError({
        code: "API_ERROR_NO_RESPONSE",
        message: "No response received from server",
        status: -1,
        data: {
          axiosErrorCode: e.code,
        },
      });
    }

    return new MoreAppError({
      code: response.data.code,
      status: response.status,
      data: response.data.data,
      rawData: response.data,
      message:
        response.data.message || "Failed to fetch or mutate data. Please contact support if this problem persists.",
      traceId: response.data.traceId,
    });
  }
  return new MoreAppError({
    code: "UNKNOWN_ERROR",
    message: "Unknown error",
    status: -1,
  });
};

export const normalizeError = (err: unknown) => {
  if (err instanceof MoreAppError || err instanceof Error) {
    return err;
  } else {
    logger.error("Unable to normalize error", { err });
    return new Error("An unexpected error occurred");
  }
};
